// Renders an external video testimonial by platform:
//   - YouTube  → inline iframe
//   - TikTok   → official embed (blockquote + embed.js)
//   - Telegram → official post widget (telegram-widget.js)
//   - other/instagram → a "Watch on …" link card (safe fallback)
// Third-party scripts are injected once and reused across cards.

import React, { useEffect, useRef } from 'react';

interface Props {
  url: string;
  platform: string | null;
}

function ensureScript(src: string, id: string) {
  if (document.getElementById(id)) {
    // Re-run TikTok/Telegram processors if the lib exposes one.
    return;
  }
  const s = document.createElement('script');
  s.id = id;
  s.async = true;
  s.src = src;
  document.body.appendChild(s);
}

function youtubeId(url: string): string | null {
  const m =
    url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|shorts\/|embed\/))([\w-]{6,})/);
  return m ? m[1] : null;
}

function tiktokId(url: string): string | null {
  const m = url.match(/\/video\/(\d+)/);
  return m ? m[1] : null;
}

function telegramPost(url: string): string | null {
  // https://t.me/<channel>/<id>  →  "<channel>/<id>"
  const m = url.match(/t\.me\/([\w\d_]+\/\d+)/);
  return m ? m[1] : null;
}

const LinkCard: React.FC<Props> = ({ url, platform }) => (
  <a className="t-embed-linkcard" href={url} target="_blank" rel="noopener noreferrer">
    ▶ Watch on {platform ? platform[0].toUpperCase() + platform.slice(1) : 'link'}
  </a>
);

const TestimonialEmbed: React.FC<Props> = ({ url, platform }) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (platform === 'tiktok') ensureScript('https://www.tiktok.com/embed.js', 'tiktok-embed-js');
    if (platform === 'telegram') ensureScript('https://telegram.org/js/telegram-widget.js?22', 'tg-embed-js');
  }, [platform, url]);

  if (platform === 'youtube') {
    const id = youtubeId(url);
    if (!id) return <LinkCard url={url} platform={platform} />;
    return (
      <div className="t-embed-video">
        <iframe
          src={`https://www.youtube.com/embed/${id}`}
          title="YouTube testimonial"
          frameBorder={0}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }

  if (platform === 'tiktok') {
    const id = tiktokId(url);
    return (
      <div className="t-embed-tiktok" ref={ref}>
        {/* eslint-disable-next-line jsx-a11y/iframe-has-title */}
        <blockquote className="tiktok-embed" cite={url} data-video-id={id || undefined}>
          <a href={url} target="_blank" rel="noopener noreferrer">
            ▶ Watch on TikTok
          </a>
        </blockquote>
      </div>
    );
  }

  if (platform === 'telegram') {
    const post = telegramPost(url);
    if (!post) return <LinkCard url={url} platform={platform} />;
    return (
      <div
        className="t-embed-telegram"
        ref={(node) => {
          // Telegram widget renders by reading data-telegram-post on a <script>.
          if (node && !node.querySelector('script')) {
            const s = document.createElement('script');
            s.async = true;
            s.src = 'https://telegram.org/js/telegram-widget.js?22';
            s.setAttribute('data-telegram-post', post);
            s.setAttribute('data-width', '100%');
            node.appendChild(s);
          }
        }}
      />
    );
  }

  return <LinkCard url={url} platform={platform} />;
};

export default TestimonialEmbed;
